import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import i18n from '../../i18n';
import { agent } from '../api/agent';
import {
  TournamentDto,
  TournamentMatchDto,
  TournamentPostDto,
  TournamentRegistrationDto,
  TournamentRefereeDto,
  TournamentSearchParams,
  TournamentSearchResultDto,
  TournamentStandingRow,
  CreateTournamentDto,
  MyTournamentNotificationDto,
} from '../models/Tournament';
import { Pagination } from '../models/Pagination';

export default class TournamentStore {
  // Lists
  searchResults: TournamentSearchResultDto[] = [];
  myOrganized: TournamentSearchResultDto[] = [];
  myParticipating: TournamentSearchResultDto[] = [];
  myNotifications: MyTournamentNotificationDto[] = [];

  // Selected tournament
  selectedTournament: TournamentDto | null = null;

  // Matches
  tournamentMatches: TournamentMatchDto[] = [];
  selectedMatch: TournamentMatchDto | null = null;

  // Standings (Round Robin / Groups)
  standings: TournamentStandingRow[] = [];

  // Posts
  posts: TournamentPostDto[] = [];

  // Referees
  referees: TournamentRefereeDto[] = [];

  // Invite user search
  invitableUsers: Array<{
    userId: string;
    fullName: string;
    imageUrl?: string;
    isAlreadyReferee: boolean;
    isAlreadyCoOrganizer: boolean;
  }> = [];
  searchingUsers = false;

  // ── Loading states ────────────────────────────────────────────────────────────
  loadingInitial = false;
  loading = false;
  searching = false;
  generatingBracket = false;

  /**
   * Granular loading key — only the action that set this key shows a spinner.
   * Format: '<action>:<id?>'  e.g. 'approveReg:abc-123', 'lifecycle:publish'
   * Use isSubmitting(key) in components instead of the old `submitting` boolean.
   */
  loadingKey: string | null = null;

  /**
   * Backward-compat getter — true when ANY action is in flight.
   * Use only for modal confirm buttons that appear alone on screen.
   * For lists of buttons, always use isSubmitting(specificKey).
   */
  get submitting() {
    return this.loadingKey !== null;
  }

  /** Returns true only when this exact key is the active loading action. */
  isSubmitting = (key: string): boolean => this.loadingKey === key;

  private setLoading = (key: string) => { this.loadingKey = key; };
  private clearLoading = () => { this.loadingKey = null; };

  // Current user ID for reactions
  private _currentUserId: string | undefined = undefined;

  pagination: Pagination | null = null;
  pagingParams = { pageNumber: 1, pageSize: 12 };

  constructor() {
    makeAutoObservable(this);
  }

  setCurrentUserId = (id: string | undefined) => {
    this._currentUserId = id;
  };

  // ── UTC helpers ──────────────────────────────────────────────────────────────

  private parseUTCDate = (value: Date | string | null | undefined): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    const str = value.toString();
    const utcStr = str.endsWith('Z') || str.includes('+') ? str : str + 'Z';
    return new Date(utcStr);
  };

  private toUTCISOString = (value: Date | string | null | undefined): string => {
    if (!value) return new Date().toISOString();
    if (value instanceof Date) return value.toISOString();
    return new Date(value).toISOString();
  };

  private toUTCISOStringOptional = (value: Date | string | null | undefined): string | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value.toISOString();
    return new Date(value).toISOString();
  };

  private processTournament = (tournament: TournamentDto): TournamentDto => ({
    ...tournament,
    registrationOpensAt: this.parseUTCDate(tournament.registrationOpensAt)?.toISOString() ?? tournament.registrationOpensAt,
    registrationClosesAt: this.parseUTCDate(tournament.registrationClosesAt)?.toISOString() ?? tournament.registrationClosesAt,
    startsAt: this.parseUTCDate(tournament.startsAt)?.toISOString() ?? tournament.startsAt,
    endsAt: this.parseUTCDate(tournament.endsAt)?.toISOString() ?? tournament.endsAt,
  });

  private processSearchResult = (result: TournamentSearchResultDto): TournamentSearchResultDto => ({
    ...result,
    startsAt: this.parseUTCDate(result.startsAt)?.toISOString() ?? result.startsAt,
  });

  private processMatch = (match: TournamentMatchDto): TournamentMatchDto => ({
    ...match,
    scheduledAt: match.scheduledAt
      ? this.parseUTCDate(match.scheduledAt)?.toISOString() ?? match.scheduledAt
      : undefined,
  });

  // ── Search / Lists ────────────────────────────────────────────────────────────

  searchTournaments = async (params: TournamentSearchParams, append = false) => {
    this.searching = true;
    try {
      const result = await agent.Tournaments.list({
        ...params,
        pageNumber: this.pagingParams.pageNumber,
        pageSize: this.pagingParams.pageSize,
      });
      runInAction(() => {
        this.searchResults = append
          ? [...this.searchResults, ...result.data.map(r => this.processSearchResult(r))]
          : result.data.map(r => this.processSearchResult(r));
        this.pagination = result.pagination;
      });
    } catch (err) {
      console.error(err);
    } finally {
      runInAction(() => { this.searching = false; });
    }
  };

  setTournamentPagingParams = (pageNumber: number) => {
    this.pagingParams = { ...this.pagingParams, pageNumber };
  };

  resetTournamentSearch = () => {
    this.searchResults = [];
    this.pagination = null;
    this.pagingParams = { pageNumber: 1, pageSize: 12 };
  };

  // ── Tournament CRUD ───────────────────────────────────────────────────────────

  loadTournament = async (id: string) => {
    this.loadingInitial = true;
    try {
      const tournament = await agent.Tournaments.getById(id);
      runInAction(() => { this.selectedTournament = this.processTournament(tournament); });
    } catch (err) {
      console.error(err);
    } finally {
      runInAction(() => { this.loadingInitial = false; });
    }
  };

  refreshTournament = async (id: string) => {
    try {
      const tournament = await agent.Tournaments.getById(id);
      runInAction(() => { this.selectedTournament = this.processTournament(tournament); });
    } catch (err) {
      console.error(err);
    }
  };

  clearSelectedTournament = () => { this.selectedTournament = null; };

  createTournament = async (data: CreateTournamentDto): Promise<string | null> => {
    const key = 'tournament:create';
    this.setLoading(key);
    try {
      const payload: CreateTournamentDto = {
        ...data,
        registrationOpensAt: this.toUTCISOString(data.registrationOpensAt),
        registrationClosesAt: this.toUTCISOString(data.registrationClosesAt),
        startsAt: this.toUTCISOString(data.startsAt),
        endsAt: this.toUTCISOStringOptional(data.endsAt),
      };
      const id = await agent.Tournaments.create(payload);
      toast.success(i18n.t('tournaments.success.created'));
      return id;
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('tournaments.errors.createFailed'));
      return null;
    } finally {
      runInAction(() => { this.clearLoading(); });
    }
  };

  updateTournament = async (id: string, data: Partial<CreateTournamentDto>): Promise<boolean> => {
    const key = 'tournament:update';
    this.setLoading(key);
    try {
      const payload: Partial<CreateTournamentDto> = { ...data };
      if (data.registrationOpensAt) payload.registrationOpensAt = this.toUTCISOString(data.registrationOpensAt);
      if (data.registrationClosesAt) payload.registrationClosesAt = this.toUTCISOString(data.registrationClosesAt);
      if (data.startsAt) payload.startsAt = this.toUTCISOString(data.startsAt);
      if (data.endsAt !== undefined) payload.endsAt = this.toUTCISOStringOptional(data.endsAt);
      await agent.Tournaments.update(id, payload);
      await this.refreshTournament(id);
      toast.success(i18n.t('tournaments.success.updated'));
      return true;
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('tournaments.errors.updateFailed'));
      return false;
    } finally {
      runInAction(() => { this.clearLoading(); });
    }
  };

  uploadLogo = async (id: string, file: File): Promise<string | null> => {
    const key = 'tournament:uploadLogo';
    this.setLoading(key);
    try {
      const url = await agent.Tournaments.uploadLogo(id, file);
      runInAction(() => {
        if (this.selectedTournament) this.selectedTournament.logoUrl = url;
      });
      toast.success(i18n.t('tournaments.success.logoUploaded'));
      return url;
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('tournaments.errors.logoUploadFailed'));
      return null;
    } finally {
      runInAction(() => { this.clearLoading(); });
    }
  };

  // ── Lifecycle actions — each gets its own key ─────────────────────────────────

  publish = async (id: string) => {
    const key = 'lifecycle:publish';
    this.setLoading(key);
    try {
      await agent.Tournaments.publish(id);
      await this.refreshTournament(id);
      toast.success(i18n.t('tournaments.success.published'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  openRegistration = async (id: string) => {
    const key = 'lifecycle:openRegistration';
    this.setLoading(key);
    try {
      await agent.Tournaments.openRegistration(id);
      await this.refreshTournament(id);
      toast.success(i18n.t('tournaments.success.registrationOpened'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  closeRegistration = async (id: string) => {
    const key = 'lifecycle:closeRegistration';
    this.setLoading(key);
    try {
      await agent.Tournaments.closeRegistration(id);
      await this.refreshTournament(id);
      toast.success(i18n.t('tournaments.success.registrationClosed'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  revertToDraft = async (id: string) => {
    const key = 'lifecycle:revertToDraft';
    this.setLoading(key);
    try {
      await agent.Tournaments.revertToDraft(id);
      await this.refreshTournament(id);
      toast.success(i18n.t('tournaments.success.revertedToDraft'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  revertToPublished = async (id: string) => {
    const key = 'lifecycle:revertToPublished';
    this.setLoading(key);
    try {
      await agent.Tournaments.revertToPublished(id);
      await this.refreshTournament(id);
      toast.success(i18n.t('tournaments.success.revertedToPublished'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  revertToRegistrationOpen = async (id: string) => {
    const key = 'lifecycle:revertToRegistrationOpen';
    this.setLoading(key);
    try {
      await agent.Tournaments.revertToRegistrationOpen(id);
      await this.refreshTournament(id);
      toast.success(i18n.t('tournaments.success.revertedToRegistrationOpen'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  generateBracket = async (id: string) => {
    this.generatingBracket = true;
    try {
      await agent.Tournaments.generateBracket(id);
      await this.refreshTournament(id);
      await this.loadMatches(id);
      toast.success(i18n.t('tournaments.success.bracketGenerated'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.generatingBracket = false; }); }
  };

  get bracketGenerated() {
    return this.tournamentMatches.length > 0;
  }

  startTournament = async (id: string) => {
    const key = 'lifecycle:start';
    this.setLoading(key);
    try {
      await agent.Tournaments.start(id);
      await this.refreshTournament(id);
      toast.success(i18n.t('tournaments.success.started'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  completeTournament = async (id: string) => {
    const key = 'lifecycle:complete';
    this.setLoading(key);
    try {
      await agent.Tournaments.complete(id);
      await this.refreshTournament(id);
      toast.success(i18n.t('tournaments.success.completed'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  cancelTournament = async (id: string, reason?: string) => {
    const key = 'lifecycle:cancel';
    this.setLoading(key);
    try {
      await agent.Tournaments.cancel(id, reason);
      await this.refreshTournament(id);
      toast.info(i18n.t('tournaments.success.cancelled'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  deleteTournament = async (id: string): Promise<boolean> => {
    const key = 'lifecycle:delete';
    this.setLoading(key);
    try {
      await agent.Tournaments.delete(id);
      runInAction(() => {
        this.myOrganized = this.myOrganized.filter(t => t.id !== id);
        if (this.selectedTournament?.id === id) this.selectedTournament = null;
      });
      toast.success(i18n.t('tournaments.success.deleted'));
      return true;
    } catch (err) {
      console.error(err);
      return false;
    } finally {
      runInAction(() => { this.clearLoading(); });
    }
  };

  // ── Match actions ─────────────────────────────────────────────────────────────

  setMatchTeams = async (matchId: string, teamAId: string | null, teamBId: string | null) => {
    const key = `match:setTeams:${matchId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.setMatchTeams(matchId, teamAId, teamBId);
      if (this.selectedTournament) await this.loadMatches(this.selectedTournament.id);
      toast.success(i18n.t('tournaments.success.matchTeamsUpdated'));
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('tournaments.errors.matchTeamsUpdateFailed'));
    } finally {
      runInAction(() => { this.clearLoading(); });
    }
  };

  populateKnockoutFromStandings = async (tournamentId: string) => {
    const key = 'match:populateKnockout';
    this.setLoading(key);
    try {
      const byGroup = new Map<number, TournamentStandingRow[]>();
      for (const row of this.standings) {
        const g = row.groupNumber ?? 0;
        if (!byGroup.has(g)) byGroup.set(g, []);
        byGroup.get(g)!.push(row);
      }
      const sortedGroups = Array.from(byGroup.entries()).sort((a, b) => a[0] - b[0]);
      const advancingPerGroup = this.selectedTournament?.teamsAdvancingPerGroup ?? 1;
      const advancing: string[] = [];
      for (let rank = 1; rank <= advancingPerGroup; rank++) {
        for (const [, rows] of sortedGroups) {
          const team = rows.find(r => r.rank === rank);
          if (team) advancing.push(team.teamId);
        }
      }
      const knockoutMatches = this.tournamentMatches
        .filter(m => m.groupNumber == null && (!m.teamAId || !m.teamBId))
        .sort((a, b) => a.round - b.round || a.matchNumber - b.matchNumber);
      for (let i = 0; i < knockoutMatches.length; i++) {
        const match = knockoutMatches[i];
        await agent.Tournaments.setMatchTeams(match.id, advancing[i * 2] ?? null, advancing[i * 2 + 1] ?? null);
      }
      await this.loadMatches(tournamentId);
      toast.success(i18n.t('tournaments.success.knockoutPopulated'));
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('tournaments.errors.knockoutPopulateFailed'));
    } finally {
      runInAction(() => { this.clearLoading(); });
    }
  };

  updateMatchResult = async (matchId: string, scoreA: number, scoreB: number) => {
    const key = `match:updateResult:${matchId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.updateMatchResult(matchId, scoreA, scoreB);
      const updated = await agent.Tournaments.getMatch(matchId);
      runInAction(() => {
        this.selectedMatch = this.processMatch(updated);
        const idx = this.tournamentMatches.findIndex(m => m.id === matchId);
        if (idx !== -1) this.tournamentMatches[idx] = this.processMatch(updated);
      });
      if (this.selectedTournament) {
        const fmt = this.selectedTournament.format;
        if (fmt === 'RoundRobin' || fmt === 'GroupStageKnockout') await this.loadStandings(this.selectedTournament.id);
      } if (this.selectedTournament) {
        const fmt = this.selectedTournament.format;
        if (fmt === 'RoundRobin' || fmt === 'GroupStageKnockout')
          await this.loadStandings(this.selectedTournament.id);
      }
      toast.success(i18n.t('tournaments.success.resultUpdated'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  advanceWinner = async (matchId: string, winnerId: string) => {
    const key = `match:advanceWinner:${matchId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.advanceWinner(matchId, winnerId);
      if (this.selectedTournament) await this.loadMatches(this.selectedTournament.id);
      toast.success(i18n.t('tournaments.success.winnerAdvanced'));
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('tournaments.errors.advanceWinnerFailed'));
    } finally {
      runInAction(() => { this.clearLoading(); });
    }
  };

  updateMatchSchedule = async (matchId: string, scheduledAt: Date, notes?: string) => {
    const key = `match:schedule:${matchId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.updateMatchSchedule(matchId, scheduledAt.toISOString(), notes);
      const updated = await agent.Tournaments.getMatch(matchId);
      runInAction(() => {
        this.selectedMatch = this.processMatch(updated);
        const idx = this.tournamentMatches.findIndex(m => m.id === matchId);
        if (idx !== -1) this.tournamentMatches[idx] = this.processMatch(updated);
      });
      toast.success(i18n.t('tournaments.success.matchScheduled'));
      return true;
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('tournaments.errors.scheduleMatchFailed'));
      return false;
    } finally {
      runInAction(() => { this.clearLoading(); });
    }
  };

  reportResult = async (matchId: string, scoreA: number, scoreB: number) => {
    const key = `match:reportResult:${matchId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.reportResult(matchId, scoreA, scoreB);
      const updated = await agent.Tournaments.getMatch(matchId);
      runInAction(() => {
        this.selectedMatch = this.processMatch(updated);
        const idx = this.tournamentMatches.findIndex(m => m.id === matchId);
        if (idx !== -1) this.tournamentMatches[idx] = this.processMatch(updated);
      });
      if (this.selectedTournament) {
        const fmt = this.selectedTournament.format;
        if (fmt === 'RoundRobin' || fmt === 'GroupStageKnockout') await this.loadStandings(this.selectedTournament.id);
      }
      toast.success(i18n.t('tournaments.success.resultReported'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  confirmResult = async (matchId: string) => {
    const key = `match:confirmResult:${matchId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.confirmResult(matchId);
      const updated = await agent.Tournaments.getMatch(matchId);
      runInAction(() => {
        this.selectedMatch = this.processMatch(updated);
        const idx = this.tournamentMatches.findIndex(m => m.id === matchId);
        if (idx !== -1) this.tournamentMatches[idx] = this.processMatch(updated);
      });
      if (this.selectedTournament) {
        const fmt = this.selectedTournament.format;
        if (fmt === 'RoundRobin' || fmt === 'GroupStageKnockout') await this.loadStandings(this.selectedTournament.id);
      }
      toast.success(i18n.t('tournaments.success.resultConfirmed'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  disputeResult = async (matchId: string, reason: string) => {
    const key = `match:dispute:${matchId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.disputeResult(matchId, reason);
      const updated = await agent.Tournaments.getMatch(matchId);
      runInAction(() => {
        this.selectedMatch = this.processMatch(updated);
        const idx = this.tournamentMatches.findIndex(m => m.id === matchId);
        if (idx !== -1) this.tournamentMatches[idx] = this.processMatch(updated);
      });
      toast.info(i18n.t('tournaments.success.resultDisputed'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  resolveDispute = async (matchId: string, scoreA: number, scoreB: number, note: string) => {
    const key = `match:resolveDispute:${matchId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.resolveDispute(matchId, scoreA, scoreB, note);
      const updated = await agent.Tournaments.getMatch(matchId);
      runInAction(() => {
        this.selectedMatch = this.processMatch(updated);
        const idx = this.tournamentMatches.findIndex(m => m.id === matchId);
        if (idx !== -1) this.tournamentMatches[idx] = this.processMatch(updated);
      });
      if (this.selectedTournament) {
        const fmt = this.selectedTournament.format;
        if (fmt === 'RoundRobin' || fmt === 'GroupStageKnockout') await this.loadStandings(this.selectedTournament.id);
      }
      toast.success(i18n.t('tournaments.success.disputeResolved'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  declareWalkover = async (matchId: string, winnerId: string, reason: string) => {
    const key = `match:walkover:${matchId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.declareWalkover(matchId, winnerId, reason);
      const updated = await agent.Tournaments.getMatch(matchId);
      runInAction(() => {
        this.selectedMatch = this.processMatch(updated);
        const idx = this.tournamentMatches.findIndex(m => m.id === matchId);
        if (idx !== -1) this.tournamentMatches[idx] = this.processMatch(updated);
      });
      if (this.selectedTournament) {
        const fmt = this.selectedTournament.format;
        if (fmt === 'RoundRobin' || fmt === 'GroupStageKnockout') await this.loadStandings(this.selectedTournament.id);
      }
      toast.info(i18n.t('tournaments.success.walkoverDeclared'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  loadMatches = async (tournamentId: string) => {
    this.loading = true;
    try {
      const matches = await agent.Tournaments.getMatches(tournamentId);
      runInAction(() => { this.tournamentMatches = matches.map(m => this.processMatch(m)); });
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.loading = false; }); }
  };

  loadMatch = async (matchId: string) => {
    this.loadingInitial = true;
    try {
      const match = await agent.Tournaments.getMatch(matchId);
      runInAction(() => { this.selectedMatch = this.processMatch(match); });
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.loadingInitial = false; }); }
  };

  clearSelectedMatch = () => { this.selectedMatch = null; };

  // ── Registrations ─────────────────────────────────────────────────────────────

  registerTeam = async (tournamentId: string, teamId: string) => {
    const key = `reg:register:${teamId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.register(tournamentId, teamId);
      await this.refreshTournament(tournamentId);
      toast.success(i18n.t('tournaments.success.teamRegistered'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  withdrawTeam = async (tournamentId: string, registrationId: string) => {
    const key = `reg:withdraw:${registrationId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.withdraw(tournamentId, registrationId);
      await this.refreshTournament(tournamentId);
      toast.info(i18n.t('tournaments.success.registrationWithdrawn'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  approveRegistration = async (tournamentId: string, registrationId: string) => {
    const key = `reg:approve:${registrationId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.approveRegistration(tournamentId, registrationId);
      await this.refreshTournament(tournamentId);
      toast.success(i18n.t('tournaments.success.registrationApproved'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  rejectRegistration = async (tournamentId: string, registrationId: string, note?: string) => {
    const key = `reg:reject:${registrationId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.rejectRegistration(tournamentId, registrationId, note);
      await this.refreshTournament(tournamentId);
      toast.info(i18n.t('tournaments.success.registrationRejected'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  updateSeed = async (tournamentId: string, registrationId: string, seed: number) => {
    const key = `reg:seed:${registrationId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.updateSeed(tournamentId, registrationId, seed);
      await this.refreshTournament(tournamentId);
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  assignGroup = async (tournamentId: string, registrationId: string, groupNumber: number) => {
    const key = `reg:group:${registrationId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.assignGroup(tournamentId, registrationId, groupNumber);
      await this.refreshTournament(tournamentId);
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  // ── Dashboard lists ───────────────────────────────────────────────────────────

  loadMyOrganized = async () => {
    this.loading = true;
    try {
      const results = await agent.Tournaments.myOrganized();
      runInAction(() => { this.myOrganized = results.map(r => this.processSearchResult(r)); });
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.loading = false; }); }
  };

  loadMyParticipating = async () => {
    this.loading = true;
    try {
      const results = await agent.Tournaments.myParticipating();
      runInAction(() => { this.myParticipating = results.map(r => this.processSearchResult(r)); });
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.loading = false; }); }
  };

  loadMyNotifications = async () => {
    try {
      const notifications = await agent.Tournaments.myNotifications();
      runInAction(() => { this.myNotifications = notifications; });
    } catch (err) { console.error(err); }
  };

  // ── Standings ─────────────────────────────────────────────────────────────────

  loadStandings = async (tournamentId: string) => {
    this.loading = true;
    try {
      const rows = await agent.Tournaments.getStandings(tournamentId);
      runInAction(() => { this.standings = rows; });
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.loading = false; }); }
  };

  // ── Referees ──────────────────────────────────────────────────────────────────

  loadReferees = async (tournamentId: string) => {
    this.loading = true;
    try {
      const refs = await agent.Tournaments.getReferees(tournamentId);
      runInAction(() => { this.referees = refs; });
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.loading = false; }); }
  };

  inviteReferee = async (tournamentId: string, userId: string) => {
    const key = `referee:invite:${userId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.inviteReferee(tournamentId, userId);
      await this.loadReferees(tournamentId);
      toast.success(i18n.t('tournaments.success.refereeInvited'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  respondRefereeInvite = async (tournamentId: string, accept: boolean) => {
    const key = `referee:respond:${tournamentId}:${accept ? 'accept' : 'decline'}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.respondRefereeInvite(tournamentId, accept);
      await this.refreshTournament(tournamentId);
      await this.loadReferees(tournamentId);
      await this.loadMyNotifications();
      toast.success(accept
        ? i18n.t('tournaments.success.refereeJoined')
        : i18n.t('tournaments.success.refereeInvitationDeclined'));
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('tournaments.errors.refereeInviteFailed'));
    } finally {
      runInAction(() => { this.clearLoading(); });
    }
  };

  approveReferee = async (tournamentId: string, refereeId: string) => {
    const key = `referee:approve:${refereeId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.approveReferee(tournamentId, refereeId);
      await this.loadReferees(tournamentId);
      toast.success(i18n.t('tournaments.success.refereeApproved'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  applyAsReferee = async (tournamentId: string) => {
    const key = `referee:apply:${tournamentId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.applyAsReferee(tournamentId);
      await this.refreshTournament(tournamentId);
      await this.loadReferees(tournamentId);
      toast.success(i18n.t('tournaments.success.refereeApplicationSubmitted'));
    } catch (err: any) {
      console.error(err);
      const message = err?.response?.data?.message || i18n.t('tournaments.errors.refereeApplicationFailed');
      toast.error(message);
    } finally {
      runInAction(() => { this.clearLoading(); });
    }
  };

  removeReferee = async (tournamentId: string, refereeId: string) => {
    const key = `referee:remove:${refereeId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.removeReferee(tournamentId, refereeId);
      await this.loadReferees(tournamentId);
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  assignRefereeToMatch = async (matchId: string, userId: string) => {
    const key = `referee:assignMatch:${matchId}:${userId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.assignRefereeToMatch(matchId, userId);
      if (this.selectedMatch?.id === matchId) await this.loadMatch(matchId);
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  // ── Co-Organizers ─────────────────────────────────────────────────────────────

  addCoOrganizer = async (tournamentId: string, userId: string) => {
    const key = `coorg:add:${userId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.addCoOrganizer(tournamentId, userId);
      await this.refreshTournament(tournamentId);
      toast.success(i18n.t('tournaments.success.coOrganizerAdded'));
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  removeCoOrganizer = async (tournamentId: string, userId: string) => {
    const key = `coorg:remove:${userId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.removeCoOrganizer(tournamentId, userId);
      await this.refreshTournament(tournamentId);
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  searchUsersToInvite = async (tournamentId: string, query: string) => {
    this.searchingUsers = true;
    try {
      const users = await agent.Tournaments.searchUsersToInvite(tournamentId, query);
      runInAction(() => { this.invitableUsers = users; });
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.searchingUsers = false; }); }
  };

  clearInvitableUsers = () => { this.invitableUsers = []; };

  // ── Posts ─────────────────────────────────────────────────────────────────────

  private _currentPostContextType: string = 'Tournament';
  private _currentPostContextId: string = '';

  private _normalizeContextType(raw: string): string {
    switch (raw.toLowerCase().replace(/[_\s-]/g, '')) {
      case 'tournament': return 'Tournament';
      case 'tournamentmatch': return 'TournamentMatch';
      case 'dispute': return 'Dispute';
      default: return raw;
    }
  }

  loadPosts = async (contextType: string, contextId: string) => {
    if (!contextType || !contextId || contextId === '00000000-0000-0000-0000-000000000000') return;
    const normalizedType = this._normalizeContextType(contextType);
    runInAction(() => {
      this._currentPostContextType = normalizedType;
      this._currentPostContextId = contextId;
      this.loading = true;
    });
    try {
      const posts = await agent.Tournaments.getPosts(normalizedType, contextId);
      runInAction(() => { this.posts = posts; });
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.loading = false; }); }
  };

  createPost = async (contextType: string, contextId: string, content: string, visibility: string) => {
    const key = 'post:create';
    this.setLoading(key);
    const normalizedType = this._normalizeContextType(contextType);
    try {
      await agent.Tournaments.createPost(normalizedType, contextId, content, visibility);
      await this.loadPosts(normalizedType, contextId);
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  deletePost = async (postId: string) => {
    const key = `post:delete:${postId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.deletePost(postId);
      runInAction(() => { this.posts = this.posts.filter(p => p.id !== postId); });
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  pinPost = async (postId: string) => {
    const key = `post:pin:${postId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.pinPost(postId);
      runInAction(() => {
        const post = this.posts.find(p => p.id === postId);
        if (post) post.isPinned = !post.isPinned;
      });
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  addReply = async (postId: string, content: string) => {
    const key = `post:reply:${postId}`;
    this.setLoading(key);
    try {
      await agent.Tournaments.addReply(postId, content);
      await this.loadPosts(this._currentPostContextType, this._currentPostContextId);
    } catch (err) { console.error(err); }
    finally { runInAction(() => { this.clearLoading(); }); }
  };

  clearPosts = () => {
    runInAction(() => {
      this.posts = [];
      this._currentPostContextType = 'Tournament';
      this._currentPostContextId = '';
    });
  };

  reactToPost = async (postId: string, type: string) => {
    // Optimistic update — no loading key needed
    runInAction(() => {
      const post = this.posts.find(p => p.id === postId);
      if (!post) return;
      const existingIdx = post.reactions.findIndex(r => r.type === type && r.userId === this._currentUserId);
      if (existingIdx !== -1) {
        post.reactions.splice(existingIdx, 1);
      } else {
        const otherIdx = post.reactions.findIndex(r => r.userId === this._currentUserId);
        if (otherIdx !== -1) post.reactions.splice(otherIdx, 1);
        post.reactions.push({ userId: this._currentUserId ?? '', type });
      }
    });
    try {
      await agent.Tournaments.reactToPost(postId, type);
    } catch (err) {
      console.error(err);
      await this.loadPosts(this._currentPostContextType, this._currentPostContextId);
    }
  };
}